import { useState, useEffect, useCallback, useRef } from 'react';
import useApi from '../hooks/useApi';
import { AUDIT_SOURCES, AUDITLOG_SQLA_MAX_DAYS, GLOBALSETTINGS, SYSTEMSETTINGS } from '../data/constants';
import { getAuditLogs } from '../services/auditLogService';
import { AuditLogDto, AuditLogRequest } from '../types/AuditLog';
import { differenceInCalendarDays, format } from 'date-fns';
import { useSettingValue } from '../services/useSettingValue';
import { DataGridProProps, useGridApiRef } from '@mui/x-data-grid-pro';
import React from 'react';
import { filterDataBySearch, handleScrollPositionChange } from '../utils/commonHelper';

/**
 * Epic 80054 - true when an Audit Log query must NOT be issued because it would scan an
 * unbounded slice of the SQLA log: Source = SQLA, a range wider than AUDITLOG_SQLA_MAX_DAYS,
 * and no user selected to scope it. Shared by the Go handler (which shows the guard message)
 * and loadData (which also runs on search/pagination changes), so the two cannot disagree.
 */
export const isSqlaRangeBlocked = (
  fromDate: Date | null,
  toDate: Date | null,
  selectedUsers: number[],
  source: string
): boolean => {
  if (source !== AUDIT_SOURCES.SQLA) return false;
  if (!fromDate || !toDate) return false;
  if ((selectedUsers?.length ?? 0) > 0) return false;
  return differenceInCalendarDays(toDate, fromDate) > AUDITLOG_SQLA_MAX_DAYS;
};

const useAuditLog = (fromDate: Date | null, toDate: Date | null, selectedUsers: number[], source: string) => {
  const { loading, executeApi } = useApi();
  const settingValue = useSettingValue(SYSTEMSETTINGS.RESOURCE_PAGINATION);
  const [isPaginationInitialized, setPaginationInitialized] = useState(false);
  const [pageState, setPageState] = useState<{ data: AuditLogDto[] | null; total: number }>({
    data: null,
    total: 0,
  });

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10),
  });
  const [handleGoTrigger, setHandleGoTrigger] = useState(-1);
  const [searchResult, setSearchResult] = useState('');
  const isClientSearchEvaluated = useRef(false);
  const allData = useRef<AuditLogDto[]>([]);
  const isFetching = useRef(false);
  const isFirstLoaded = useRef(false);
  const mounted = useRef(true);
  const apiRef = useGridApiRef();

  React.useEffect(() => {
    apiRef?.current?.subscribeEvent('scrollPositionChange', handleScrollPositionChange);
    return () => {
      mounted.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const prevPagination = useRef(paginationModel);
  const prevSearchResult = useRef(searchResult);
  const isSourceMounted = useRef(false);

  const handleOnRowsScrollEnd: DataGridProProps['onRowsScrollEnd'] = () => {
    if (loading || !pageState?.data) return; // Avoid multiple triggers
    if (isFetching.current) return;

    if (pageState?.data && pageState?.data.length >= pageState?.total) {
      return; // Prevent further API calls when all rows are loaded
    }

    isFetching.current = true;
    setPaginationModel((prev) => {
      const totalPages = Math.ceil(pageState?.total / prev.pageSize);
      if (prev.page + 1 < totalPages) {
        return { ...prev, page: prev.page + 1 };
      }
      return prev;
    });

    // Restore previous scroll position after data updates
    setTimeout(() => {
      isFetching.current = false;
    }, 100);
  };

  const handleSearchText = useCallback((text: string) => {
    setSearchResult((prev) => (prev !== text ? text : prev));
    setPaginationModel((prev) => ({
      ...prev,
      page: 0,
    }));
  }, []);


  const loadData = useCallback(async () => {
    if (!fromDate || !toDate) return;
    //* Epic 80054 - handleGoClick blocks this combination before it ever triggers a fetch,
    //* but loadData also runs on search-text and pagination changes, which would still fire
    //* the unbounded SQLA query. Guarding here too means no code path can issue it. Returning
    //* without touching pageState leaves the grid as-is and never sets `loading`, so no
    //* overlay appears.
    if (isSqlaRangeBlocked(fromDate, toDate, selectedUsers, source)) return;
    const isNewSearch = paginationModel.page === 0;
    if (isClientSearchEvaluated.current && isFirstLoaded.current) {
      if (searchResult) {
        const lowerText = searchResult.toLowerCase();
        const filtered = filterDataBySearch(allData.current, lowerText);
        setPageState({
          data: filtered ?? [],
          total: filtered?.length,
        });
      } else {
        // If no search term, reset to full data
        setPageState({
          data: allData.current ?? [],
          total: allData.current?.length,
        });
      }
    }
    else {
      const auditLogRequest: AuditLogRequest = {
        fromDate: format(fromDate, GLOBALSETTINGS.DATE_FORMAT_ISO),
        toDate: format(toDate, GLOBALSETTINGS.DATE_FORMAT_ISO),
        searchTerm: searchResult,
        pageNumber: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        userIds: selectedUsers ?? [],
        source
      }
      const auditLogData = await executeApi<AuditLogRequest, AuditLogDto[]>(getAuditLogs, auditLogRequest);
      if (auditLogData && auditLogData.length > 0) {
        if (!isFirstLoaded.current && auditLogData[0]?.totalRecords <= paginationModel.pageSize) {
          isClientSearchEvaluated.current = true;
          allData.current = auditLogData;
        }
        isFirstLoaded.current = true;

        setPageState((prev) => {
          const existingIds = new Set(prev.data?.map((row) => row.auditLogId)); // Track existing IDs
          const newData = auditLogData.filter((row) => !existingIds.has(row.auditLogId)); // Filter duplicates

          return {
            data: isNewSearch ? auditLogData : [...(prev.data || []), ...newData],
            total: auditLogData.length > 0 && isNewSearch ? auditLogData[0].totalRecords : prev.total,
          };
        });
      }
      else {
        setPageState({
          data: [],
          total: 0,
        });
      }
    }
    setTimeout(() => {
      apiRef?.current?.scrollToIndexes({ rowIndex: apiRef.current.getScrollPosition().top });
    }, 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedUsers, paginationModel, searchResult, source]);

  useEffect(() => {
    if (settingValue) {
      const newPageSize = parseInt(settingValue ?? GLOBALSETTINGS.DEFAULT_VALUE, 10);

      setPaginationModel((prev) => {
        if (prev.pageSize !== newPageSize) {
          return { page: 0, pageSize: newPageSize };
        }
        return prev;
      });
      setPaginationInitialized(true);
    }
  }, [settingValue]);


  useEffect(() => {
    if (!isSourceMounted.current) {
      isSourceMounted.current = true;
      return;
    }
    isClientSearchEvaluated.current = false;
    isFirstLoaded.current = false;
    allData.current = [];
  }, [source]);

  useEffect(() => {
    if (isPaginationInitialized && handleGoTrigger > -1) {
      isClientSearchEvaluated.current = false;
      allData.current = [];
      isFirstLoaded.current = false;
      if (prevPagination.current.page === 0) {
        loadData();
      }
      else {
        setPaginationModel((prev) => ({
          ...prev,
          page: 0,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleGoTrigger, isPaginationInitialized]);

  useEffect(() => {
    const hasChanged = JSON.stringify(prevPagination.current) !== JSON.stringify(paginationModel);
    const searchChanged = JSON.stringify(prevSearchResult.current) !== JSON.stringify(searchResult);
    if (isPaginationInitialized || hasChanged || searchChanged) {
      prevPagination.current = paginationModel;
      prevSearchResult.current = searchResult;
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPaginationInitialized, paginationModel, searchResult])

  return { loading, pageState, paginationModel, setHandleGoTrigger, handleOnRowsScrollEnd, apiRef, handleSearchText };
};

export default useAuditLog;
